import { mapGetters, mapActions } from 'vuex'
import parseData from '@/utils/parseData'
import tooltips from '@/utils/tooltips'

import KPI from '@/components/KPI/KPI.vue'
import ChartComponent from '@/components/Chart/Chart.vue'

export default {
  name: 'Home',
  data () {
    return {
      showSetupMessage: false,
      isLoading: {
        gsc: false,
        stats: false,
        chart: false,
        sprints: false
      },

      taskFilters: [
        { text: 'All', value: 'all' },
        { text: 'New', value: 'new' },
        { text: 'Updated', value: 'update' }
      ],

      clicksLast: null,
      clicksPrev: null,
      imprLast: null,
      imprPrev: null,
      nrOfKeywordsLast: null,
      nrOfKeywordsPrev: null,
      statsTotalPages: null,
      statsPublishedPages: null,
      statsIndexedPages: null,
      geckoClicksLast30Days: null,
      geckoClicksPrev30Days: null,

      sprints: [],
      selectedSprintId: 'all',
      taskFilterIndex: 0,

      chartVersion: 0,
      monthlyChartData: null,
      monthlyChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'bottom',
          labels: { boxWidth: 10, fontColor: '#687178', usePointStyle: true }
        },
        tooltips: { mode: 'index', intersect: false },
        scales: {
          xAxes: [{ stacked: true, gridLines: { display: false } }],
          yAxes: [{ stacked: true, ticks: { beginAtZero: true, callback: (value) => value.toLocaleString('en-US') } }]
        }
      }
    }
  },
  watch: {
    async $route () {
      await this.loadData()
    },
    selectedSprintId () {
      this.taskFilterIndex = 0
    }
  },
  computed: {
    ...mapGetters([
      'articles',
      'activeContentplan',
      'activeWorkspace',
      'gsc',
      'pages',
      'keywords'
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    contentplanName () {
      return this.activeContentplan ? this.activeContentplan.name : ''
    },
    clicksChange () {
      if (!this.clicksLast || !this.clicksPrev) return 0
      const changePercentage = parseData(((this.clicksLast - this.clicksPrev) / this.clicksPrev) * 100)
      return Math.round(changePercentage)
    },
    imprChange () {
      if (!this.imprLast || !this.imprPrev) return 0
      const changePercentage = parseData(((this.imprLast - this.imprPrev) / this.imprPrev) * 100)
      return Math.round(changePercentage)
    },
    nrOfKeywordsChange () {
      if (!this.nrOfKeywordsLast || !this.nrOfKeywordsPrev) return 0
      const changePercentage = parseData(((this.nrOfKeywordsLast - this.nrOfKeywordsPrev) / this.nrOfKeywordsPrev) * 100)
      return Math.round(changePercentage)
    },
    geckoClicksChange () {
      if (!this.geckoClicksLast30Days || !this.geckoClicksPrev30Days) return 0
      return Math.round(((this.geckoClicksLast30Days - this.geckoClicksPrev30Days) / this.geckoClicksPrev30Days) * 100)
    },
    sprintsWithTasks () {
      return this.sprints
        .filter(sprint => sprint.tasks?.length)
        .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')) || b.id - a.id)
    },
    totalSprintTasks () {
      return this.sprintsWithTasks.reduce((total, sprint) => total + sprint.tasks.length, 0)
    },
    selectedSprintTasks () {
      const sprints = this.selectedSprintId === 'all' ? this.sprintsWithTasks : this.sprintsWithTasks.filter(sprint => sprint.id === this.selectedSprintId)
      return sprints
        .flatMap(sprint => sprint.tasks.map(task => this.toSprintRow(task, sprint.name)))
        .sort((a, b) => b.sortDate.localeCompare(a.sortDate))
    },
    taskFilter () {
      return this.taskFilters[this.taskFilterIndex]?.value || 'all'
    },
    filteredTasks () {
      if (this.taskFilter === 'all') return this.selectedSprintTasks
      return this.selectedSprintTasks.filter(row => row.type === this.taskFilter)
    },
    sprintTableHeaders () {
      return [
        { text: 'Date', value: 'date' },
        ...(this.selectedSprintId === 'all' ? [{ text: 'Sprint', value: 'sprintName' }] : []),
        { text: 'Change', value: 'type' },
        { text: 'URL', value: 'url' },
        { text: 'Google', value: 'google' },
        { text: 'Clicks 30d', value: 'clicks', align: 'end' }
      ]
    },
    remainingCredits () {
      return this.activeWorkspace?.remaining_article_credits
    },
    publishedWidthPercent () {
      if (!this.statsTotalPages || this.statsTotalPages === 0) return 100
      return Math.max((this.statsPublishedPages / this.statsTotalPages) * 100, 20)
    },
    indexedWidthPercent () {
      if (!this.statsTotalPages || this.statsTotalPages === 0) return 100
      return Math.max((this.statsIndexedPages / this.statsTotalPages) * 100, 20)
    }
  },
  methods: {
    parseData,
    tooltips,
    ...mapActions([
      'articleGet',
      'sprintsGet',
      'getGSCReportBundle',
      'getIntegrations',
      'setSnackbar',
      'contentplanGetOne'
    ]),
    async loadData () {
      this.isLoading.gsc = true
      document.title = 'Home | ContentGecko'

      // Check if we should show setup completion message
      if (this.$route.query.showSetupMessage === 'true') {
        this.showSetupMessage = true
        // Clean up the URL parameter
        this.$router.replace({
          name: this.$route.name,
          params: this.$route.params
        })
      }

      try {
        // Load contentplan first to check for GSC integration
        await this.contentplanGetOne({ contentplanId: this.contentplanId })

        if (!this.activeContentplan?.gsc_property) {
          this.setSnackbar({ open: true, text: 'No GSC integration', type: 'error' })
          this.clicksLast = 0
          this.clicksPrev = 0
          this.imprLast = 0
          this.imprPrev = 0
          this.nrOfKeywordsLast = 0
          this.nrOfKeywordsPrev = 0
          this.geckoClicksLast30Days = 0
          this.geckoClicksPrev30Days = 0
          this.isLoading.gsc = false
          await Promise.all([
            this.getIntegrations({ contentplanId: this.contentplanId }),
            this.populateSprints(),
            this.populateStats()
          ])
        } else {
          const gscTasks = [
            this.populateClicksAndImpressions(),
            this.populateKeywordCount()
          ]
          gscTasks.push(this.populateGeckoTrafficKPIs())

          await Promise.all([
            ...gscTasks,
            this.populateMonthlyClicksChart(),
            this.getIntegrations({ contentplanId: this.contentplanId }),
            this.populateSprints(),
            this.populateStats()
          ])
          this.isLoading.gsc = false
        }
      } catch (error) {
        console.error('Error loading data:', error)
        this.isLoading.gsc = false
      }
    },
    dismissSetupMessage () {
      this.showSetupMessage = false
    },
    async populateSprints () {
      this.isLoading.sprints = true
      try {
        const sprints = await this.sprintsGet({ contentplanId: this.contentplanId })
        this.sprints = Array.isArray(sprints) ? sprints : []
        this.selectedSprintId = this.sprintsWithTasks[0]?.id || 'all'
      } catch (error) {
        console.error('Error populating sprints:', error)
      } finally {
        this.isLoading.sprints = false
      }
    },
    toSprintRow (task, sprintName) {
      const rawDate = task.page_last_cg_write_at || task.latest_page_published_at || task.status_changed_at || ''
      const clicks = task.page_clicks_last
      return {
        task_id: task.task_id,
        sprintName,
        type: task.type,
        title: task.title,
        url: task.article_url,
        sortDate: rawDate,
        date: rawDate ? new Date(rawDate.replace(' ', 'T')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-',
        google: this.googleStatus(task),
        clicks: clicks === null || clicks === undefined ? '-' : clicks.toLocaleString('en-US')
      }
    },
    googleStatus (task) {
      if (!task.article_url) return 'Not published'
      if (!task.page_impressions_last) return 'Not indexed'
      return `Pos ${Math.round(task.page_position_last)}`
    },
    taskCount (type) {
      return this.selectedSprintTasks.filter(row => row.type === type).length
    },
    async populateMonthlyClicksChart () {
      this.isLoading.chart = true
      try {
        const today = new Date()
        const startDate = this.formatDate(new Date(today.getFullYear(), today.getMonth() - 12, 1))
        const endDate = this.formatDate(new Date(today.getFullYear(), today.getMonth(), 0))
        const request = (onlyContentGeckoPages) => this.getGSCReportBundle({
          contentplanId: this.contentplanId,
          startDate,
          endDate,
          aggregation: 'monthly',
          country: 'all',
          onlyContentGeckoPages,
          sections: ['DATE'],
          commitToStore: false
        })
        const [allPages, geckoPages] = await Promise.all([request(false), request(true)])
        const toMonthMap = (rows = []) => new Map(rows.map(row => [String(row.keys?.[0] || '').slice(0, 7), row.clicks || 0]))
        const allByMonth = toMonthMap(allPages?.byDate)
        const geckoByMonth = toMonthMap(geckoPages?.byDate)
        const months = Array.from(allByMonth.keys())
        const geckoClicks = months.map(month => geckoByMonth.get(month) || 0)

        this.monthlyChartData = {
          labels: months.map(month => new Date(`${month}-01T00:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })),
          datasets: [
            { label: 'ContentGecko pages', backgroundColor: '#112244', data: geckoClicks, barPercentage: 0.7 },
            { label: 'Rest of site', backgroundColor: '#cfd6de', data: months.map((month, i) => Math.max(allByMonth.get(month) - geckoClicks[i], 0)), barPercentage: 0.7 }
          ]
        }
        this.chartVersion++
      } catch (error) {
        console.error('Error populating monthly clicks chart:', error)
      } finally {
        this.isLoading.chart = false
      }
    },
    async populateStats () {
      this.isLoading.stats = true
      try {
        const filters = [
          { field: 'final', op: 'is_null', value: false },
          { field: 'showInMap', op: 'eq', value: 1 }
        ]
        const getStatsTotal = (extraFilters = []) => this.articleGet({
          contentplanId: this.contentplanId,
          filters: [...filters, ...extraFilters],
          pagination: { limit: 1, offset: 0 },
          straightReturn: true
        })
        const [total, published, indexed] = await Promise.all([
          getStatsTotal(),
          getStatsTotal([{ field: 'status', op: 'eq', value: 'published' }]),
          getStatsTotal([{ field: 'pageImpressionsLast', op: 'gte', value: 1 }])
        ])

        this.statsTotalPages = total?.meta?.totalCount || 0
        this.statsPublishedPages = published?.meta?.totalCount || 0
        this.statsIndexedPages = indexed?.meta?.totalCount || 0
      } catch (error) {
        console.error('Error populating stats:', error)
      }
      this.isLoading.stats = false
    },
    async populateClicksAndImpressions () {
      const TIMEFRAME = 30
      const DAYS = TIMEFRAME * 2
      const OFFSET = 0

      if (!this.activeContentplan?.gsc_property) {
        return
      }

      try {
        const [startDate, endDate] = this.setDates(DAYS, OFFSET)

        await this.getGSCReportBundle({
          contentplanId: this.contentplanId,
          startDate,
          endDate,
          aggregation: 'daily',
          country: 'all',
          sections: ['DATE']
        })

        const dataTypes = ['clicks', 'impressions']
        const [clicksData, imprData] = dataTypes.map(this.aggregateData)

        this.clicksPrev = clicksData[0]
        this.clicksLast = clicksData[1]
        this.imprPrev = imprData[0]
        this.imprLast = imprData[1]
      } catch (error) {
        console.error('Error populating clicks and impressions:', error)
      }
    },
    async populateKeywordCount () {
      const TIMEFRAME = 30
      const OFFSET = 0

      if (!this.activeContentplan?.gsc_property) {
        return
      }

      try {
        // Set dates for last period and previous period
        const [startDate1, endDate1] = this.setDates(TIMEFRAME, OFFSET)
        const [startDate2, endDate2] = this.setDates(TIMEFRAME * 2, TIMEFRAME)

        const [countLast, countPrev] = await Promise.all([
          this.fetchQueryCount(startDate1, endDate1),
          this.fetchQueryCount(startDate2, endDate2)
        ])

        this.nrOfKeywordsLast = countLast
        this.nrOfKeywordsPrev = countPrev
      } catch (error) {
        console.error('Error populating keyword count:', error)
      }
    },
    async fetchQueryCount (startDate, endDate) {
      const PAGE_SIZE = 2000
      const MAX_PAGES = 10
      const uniqueQueries = new Set()
      let queryCursor = 0
      let page = 0

      while (page < MAX_PAGES) {
        const response = await this.getGSCReportBundle({
          contentplanId: this.contentplanId,
          startDate,
          endDate,
          country: 'all',
          sections: ['QUERY'],
          queryCursor,
          queryLimit: PAGE_SIZE,
          commitToStore: false
        })

        const rows = response?.byQuery || []
        rows.forEach(row => uniqueQueries.add((row?.keys || []).join('||')))

        const nextCursor = response?.pagination?.queryNextCursor
        page++
        if (!Number.isInteger(nextCursor)) break
        queryCursor = nextCursor
      }

      return uniqueQueries.size
    },
    async populateGeckoTrafficKPIs () {
      const TIMEFRAME = 30
      const OFFSET = 0

      if (!this.activeContentplan?.gsc_property) {
        return
      }

      try {
        const [startDate, endDate] = this.setDates(TIMEFRAME * 2, OFFSET)

        const response = await this.getGSCReportBundle({
          contentplanId: this.contentplanId,
          startDate,
          endDate,
          aggregation: 'daily',
          country: 'all',
          onlyContentGeckoPages: true,
          sections: ['DATE'],
          commitToStore: false
        })
        const data = response?.byDate || []
        const [lastStartDate] = this.setDates(TIMEFRAME, OFFSET)
        const sumClicks = (rows) => rows.reduce((total, item) => total + (item.clicks || 0), 0)

        this.geckoClicksPrev30Days = sumClicks(data.filter(item => item.keys?.[0] < lastStartDate))
        this.geckoClicksLast30Days = sumClicks(data.filter(item => item.keys?.[0] >= lastStartDate))
      } catch (error) {
        console.error('Error populating Gecko traffic KPIs:', error)
        this.geckoClicksLast30Days = 0
      }
    },
    aggregateData (dataType) {
      if (!this.gsc || !this.gsc.byDate) return [0, 0]

      let firstSum, secondSum
      try {
        const { byDate } = this.gsc
        const half = Math.ceil(byDate.length / 2)
        const [firstHalf, secondHalf] = [byDate.slice(0, half), byDate.slice(half)]

        const aggregate = (data, type) => data.reduce((total, item) => total + item[type], 0)

        firstSum = aggregate(firstHalf, dataType)
        secondSum = aggregate(secondHalf, dataType)
      } catch (error) {
        console.error('Error aggregating data:', error)
      }
      return [firstSum, secondSum]
    },
    setDates (startDayOffset, endDayOffset) {
      const GSC_MIN_OFFSET = 3
      const today = new Date()

      const startDate = new Date()
      startDate.setDate(today.getDate() - startDayOffset - GSC_MIN_OFFSET + 1)

      const endDate = new Date()
      endDate.setDate(today.getDate() - endDayOffset - GSC_MIN_OFFSET)

      return [this.formatDate(startDate), this.formatDate(endDate)]
    },
    formatDate (date) {
      const year = date.getFullYear()
      // JavaScript months are 0-based, so +1 is added to get the correct month number
      const month = ('0' + (date.getMonth() + 1)).slice(-2)
      const day = ('0' + date.getDate()).slice(-2)
      return `${year}-${month}-${day}`
    }
  },
  async mounted () {
    await this.loadData()
  },
  components: {
    KPI,
    ChartComponent
  }
}
